import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, Store, HeartPulse, GraduationCap, Trophy, Landmark, Building, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useAuth } from "@/context/AuthContext";

const KIND_ICON = {
  airport:  Plane,
  mall:     Store,
  hospital: HeartPulse,
  school:   GraduationCap,
  stadium:  Trophy,
  expo:     Landmark,
  hotel:    Building,
  other:    Building,
};

const KIND_LABEL = {
  airport: "Airport", mall: "Mall", hospital: "Hospital", school: "School",
  stadium: "Stadium", expo: "Expo Hall", hotel: "Hotel", other: "Venue",
};

/**
 * /venues — Universal Venue directory (Slice H).
 * Groups by kind with tap-through to /venue/:slug.
 */
export default function Venues() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useSeo({
    title: "Venue maps — Find2Near",
    description: "Interactive kiosk-style floor plans of airports, malls, hospitals and more.",
  });

  const load = () => {
    setLoading(true);
    api.get("/venues").then(r => setItems(r.data?.items || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const seedDemo = () => {
    setSeeding(true);
    api.post("/venues/seed-demo").then(load).finally(() => setSeeding(false));
  };

  const grouped = items.reduce((acc, v) => {
    (acc[v.kind || "other"] ||= []).push(v);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-1">Slice H · Venue Map</div>
          <h1 className="heading font-bold text-3xl md:text-4xl text-stone-900">Venue maps</h1>
          <p className="text-sm text-stone-600 mt-2 max-w-lg">
            Interactive floor plans for airports, malls, hospitals, schools and more.
            Kiosk-friendly — add <code className="bg-stone-100 px-1 rounded">?kiosk=1</code> to any map for fullscreen mode.
          </p>
        </div>
        {items.length === 0 && !loading && user?.is_admin && (
          <button
            onClick={seedDemo}
            disabled={seeding}
            className="text-xs font-bold rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white px-4 py-2 disabled:opacity-60"
            data-testid="venues-seed-demo"
          >
            {seeding ? "Seeding…" : "Seed demo terminal"}
          </button>
        )}
        <Link
          to="/venue-owner"
          className="text-xs font-bold rounded-full border border-stone-300 hover:border-stone-400 text-stone-700 bg-white px-4 py-2"
          data-testid="venues-owner-portal-link"
        >
          Publish your venue →
        </Link>
      </div>

      {loading && (
        <div className="text-stone-500 text-sm" data-testid="venues-loading">Loading venues…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-16 bg-white border border-stone-200 rounded-2xl" data-testid="venues-empty">
          <MapPin size={32} className="mx-auto text-stone-300 mb-3" />
          <div className="heading font-bold text-lg text-stone-900">No venues published yet</div>
          <div className="text-sm text-stone-500 mt-1">Seed the demo terminal above to preview the flow.</div>
        </div>
      )}

      {!loading && Object.entries(grouped).map(([kind, list]) => {
        const Ico = KIND_ICON[kind] || Building;
        return (
          <section key={kind} className="mb-8" data-testid={`venues-group-${kind}`}>
            <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-3 flex items-center gap-1.5">
              <Ico size={13} /> {KIND_LABEL[kind] || kind}s
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map(v => (
                <Link
                  key={v.venue_id}
                  to={`/venue/${v.slug}`}
                  className="block bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 hover:shadow-md transition-shadow"
                  data-testid={`venue-card-${v.slug}`}
                >
                  <div className="font-bold text-stone-900">{v.name}</div>
                  <div className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                    <MapPin size={11} /> {[v.city, v.country].filter(Boolean).join(", ") || "Location"}
                    {v.floors?.length ? <span>· {v.floors.length} floors</span> : null}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
